import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { BlockCategory } from 'src/forms/AdsForm/types'

import { Classified } from '../../rest/types/Classified'
import { ListDisplayType } from '../../types/classified'
import { humanDate } from '../../helpers/DateHelpers'
import { ActionButtonVariant } from '../../components/ActionButton/styled'
import routes from '../../routes/definitions'
import Router from '../../routes/Router'
import MapModal from '../../components/MapModal'
import { classifiedCardTransformer } from '../../rest/transformers/classifiedTransformer'
import { useIsFront } from '../../hooks'
import { AdsCardVariant } from '../../components/AdsCard/styled'

const useAdsListDisplayTypes = ({
  classifieds,
  displayTypeParams,
}: {
  classifieds: Array<Classified> | null | undefined
  displayTypeParams: ListDisplayType
}) => {
  const { t } = useTranslation()
  const [activeMarker, setActiveMarker] = useState<number | null>(null)
  const ready = useIsFront()

  const safeClassifieds: Array<Classified> = Array.isArray(classifieds)
    ? classifieds
    : []

  return {
    ...(displayTypeParams === ListDisplayType.List && {
      adsList: safeClassifieds.map((classified, idx) => ({
        columns: [
          {
            ...(idx === 0 && {
              label: t('listingAdsColumnTitleLabel'),
            }),
            value:
              classified?.classifiedCategory?.form === BlockCategory.Liberal &&
              classified?.user?.speciality?.name
                ? `${classified.title} (${classified.user?.speciality.name})`
                : classified?.title ?? '',
          },
          {
            ...(idx === 0 && {
              label: t('listingAdsColumnLocalisationLabel'),
            }),
            value: classified?.city ?? '',
          },
          {
            ...(idx === 0 && {
              label: t('listingAdsColumnOfferLabel'),
            }),
            value: classified?.classifiedCategory?.name ?? '',
          },

          {
            ...(idx === 0 && {
              label: t('listingAdsColumnDateLabel'),
            }),
            value: humanDate(classified.createdAt ?? ''),
            shrink: true,
          },
        ],
        actions: [
          {
            text: t('listingAdsColumnActionLearnMore'),
            variant: ActionButtonVariant.tertiary,
            href: Router.getRouteUrl(routes.classified, {
              slug: classified.slug,
            }),
          },
        ],
      })),
    }),
    ...(displayTypeParams === ListDisplayType.Map &&
      ready && {
        map: {
          fitBounds: true,
          onClick: () => setActiveMarker(null),
          options: {
            disableDoubleClickZoom: true,
            disableDefaultUI: true,
            scaleControl: false,
            rotateControl: false,
            zoomControl: false,
            keyboardShortcuts: false,
            scrollwheel: false,
          },
          markers: safeClassifieds.map((classified, idx) => ({
            onClick: () => setActiveMarker(idx),
            position: {
              lat: classified.lat ?? 0,
              lng: classified.long ?? 0,
            },
            content:
              activeMarker === idx ? (
                <MapModal
                  title={classified.title ?? ''}
                  address={`${classified.city} (${classified.postalCode})`}
                  category={classified?.classifiedCategory?.name ?? ''}
                  date={t('listingAdsMapUpdatedAt', {
                    date: humanDate(classified.updatedAt ?? ''),
                  })}
                  onClose={() => setActiveMarker(null)}
                  display={activeMarker === idx}
                  position={{
                    lat: classified.lat ?? 0,
                    lng: classified.long ?? 0,
                  }}
                  link={{
                    text: t('listingAdsMapSeeMore'),
                    href: Router.getRouteUrl(routes.classified, {
                      slug: classified.slug,
                    }),
                    target: '_blank',
                  }}
                />
              ) : (
                <></>
              ),
          })),
        },
      }),
    ...(displayTypeParams === ListDisplayType.Card && {
      adsGrid: {
        items: safeClassifieds.map((classified) => ({
          ...classifiedCardTransformer({
            data: classified,
            t,
            contentVariant: 'withDescription',
            colorVariant: AdsCardVariant.secondary,
          }),
        })),
      },
    }),
  }
}

export default useAdsListDisplayTypes
